/**
 * Visualize Statistics bar chart for one attribute column.
 */
import { useEffect, useRef } from 'react';
import { Button, Icon, Stack, Text, tokens } from '@/design';
import type { ColumnChartSeries } from './attributeTableStats';

export interface AttributeChartPanelProps {
  readonly series: ColumnChartSeries;
  readonly onClose?: () => void;
  readonly 'data-testid'?: string;
}

export function AttributeChartPanel({
  series,
  onClose,
  'data-testid': testId = 'attribute-chart-panel',
}: AttributeChartPanelProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<{ destroy?: () => void } | null>(null);

  useEffect(() => {
    if (!canvasRef.current || series.bars.length === 0) return;
    let cancelled = false;

    (async () => {
      try {
        const { Chart, registerables } = await import('chart.js');
        if (cancelled) return;
        Chart.register(...registerables);
        chartRef.current?.destroy?.();
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;
        chartRef.current = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: series.bars.map((bar) => bar.label),
            datasets: [
              {
                label: series.yLabel,
                data: series.bars.map((bar) => bar.count),
                backgroundColor: `${tokens.color.accent.primary}99`,
                borderColor: tokens.color.accent.primary,
                borderWidth: 1,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              title: {
                display: true,
                text: series.title,
                color: tokens.color.ink.primary,
              },
            },
            scales: {
              x: {
                title: { display: true, text: series.xLabel, color: tokens.color.ink.secondary },
                ticks: {
                  color: tokens.color.ink.secondary,
                  maxRotation: 45,
                  minRotation: 45,
                },
                grid: { color: tokens.color.surface[3] },
              },
              y: {
                title: { display: true, text: series.yLabel, color: tokens.color.ink.secondary },
                ticks: { color: tokens.color.ink.secondary, precision: 0 },
                beginAtZero: true,
                grid: { color: tokens.color.surface[3] },
              },
            },
          },
        });
      } catch {
        /* Chart.js is optional in SSR tests. */
      }
    })();

    return () => {
      cancelled = true;
      chartRef.current?.destroy?.();
      chartRef.current = null;
    };
  }, [series]);

  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[3],
        minHeight: 0,
        flex: '1 1 auto',
      }}
    >
      <Stack direction="row" align="center" justify="space-between">
        <Text variant="subtitle" as="h3" style={{ margin: 0 }}>
          {series.title}
        </Text>
        {onClose ? (
          <Button variant="ghost" onClick={onClose} data-testid={`${testId}-close`}>
            <Icon name="close" size={20} decorative />
            <span>Close chart</span>
          </Button>
        ) : null}
      </Stack>
      <div style={{ flex: '1 1 auto', minHeight: 320, position: 'relative' }}>
        <canvas ref={canvasRef} aria-label={series.title} />
      </div>
    </div>
  );
}
